/** World creation, roster entry and placement publication. */
import type {
  CountryId,
  CountryState,
  CrisisState,
  PlacementPublication,
  RosterEntry,
  SlotId,
  WorldConfig,
  WorldState,
  Zone,
} from '../shared/types.ts';
import { IDEOLOGY_IDS, UNIT_TYPES } from '../shared/types.ts';
import { IDEOLOGIES } from './data/ideologies.ts';
import { BASE, HEAT } from './data/rules.ts';
import { SLOTS } from './data/slots.ts';
import { CLASSIC_WORLD, CRISES, DEFAULT_BRIEFING_MS, DEFAULT_CONFIG, DEFAULT_ORDERS_MS, PAIRINGS } from './data/world.ts';
import { ensureAgendas } from './agenda-hooks.ts';
import { emptyNotes, emptyStats } from './core.ts';
import { Rng, deriveSeed } from './rng.ts';
import { redealRoles } from './roles.ts';
import { clamp, forcesTotal, nationalIndex } from './util.ts';
import { TEMPERAMENTS } from '../shared/types.ts';

export interface CreateWorldOptions {
  id: string;
  className: string;
  seed: number;
  now: number;
  config?: Partial<WorldConfig>;
  /** Ideology -> slot. Defaults to the pairing named in the config (the Classic World). */
  pairing?: Partial<Record<CountryId, SlotId>>;
}

function makeCountry(id: CountryId, slot: SlotId, rng: Rng): CountryState {
  const i = IDEOLOGIES[id];
  const sl = SLOTS[slot];
  const forces = { army: 0, navy: 0, air: 0, defenses: 0 };
  for (const u of UNIT_TYPES) forces[u] = Math.max(0, BASE.forces[u] + (i.start.forces[u] ?? 0) + (sl.start.forces[u] ?? 0));
  const idx = (k: 'prosperity' | 'security' | 'wellbeing' | 'environment') => clamp(BASE.index + i.start.index[k] + sl.start.index[k], 0, 100);
  const c: CountryState = {
    id,
    slot,
    isAI: true,
    temperament: rng.pick(TEMPERAMENTS),
    treasury: BASE.treasury + i.start.treasury + sl.start.treasury,
    energy: BASE.energy + i.start.energy + sl.start.energy,
    food: BASE.food + i.start.food + sl.start.food,
    support: i.start.support,
    prosperity: idx('prosperity'),
    security: idx('security'),
    wellbeing: Math.min(idx('wellbeing'), id === 'kestran' ? 85 : 100),
    environment: idx('environment'),
    carbon: sl.carbon,
    resilience: sl.resilience,
    cohesion: BASE.cohesion,
    cleanEnergy: 0,
    lockedFund: i.start.lockedFund,
    fundUnlocked: false,
    escrow: { treasury: 0, energy: 0, food: 0 },
    forces,
    posture: 'ready',
    queue: [],
    programs: [],
    diversification: 0,
    adapt: { earlyWarning: false, heatSafe: false, mangrove: false },
    conservationUsed: 0,
    forestCleared: 0,
    lastFossilTurn: -99,
    freeGreenUsed: false,
    abilityUsed: false,
    lastPlanTurn: -99,
    royalGrants: id === 'aurem',
    effects: [],
    tariffs: [],
    embargoes: [],
    transparency: [],
    treatyPartnersEver: [],
    planProsperity: 0,
    factionMood: 0,
    factionLines: [],
    start: { treasury: 0, carbon: sl.carbon, wellbeing: 0, forcesTotal: forcesTotal(forces), forces: { ...forces }, index: 0 },
    objectives: i.objectives,
    stats: emptyStats(),
    history: [],
    intel: [],
    islandDeveloped: false,
    notes: emptyNotes(),
  };
  c.start.treasury = c.treasury;
  c.start.wellbeing = c.wellbeing;
  c.start.index = nationalIndex(c);
  c.planProsperity = c.prosperity;
  return c;
}

function makeZones(ids: CountryId[], pairing: Record<CountryId, SlotId>): Zone[] {
  const zones: Zone[] = [];
  for (const id of ids) {
    const names = SLOTS[pairing[id]].zoneNames;
    names.forEach((name, k) => zones.push({ id: `${id}-${k + 1}`, home: id, holder: id, name, capital: k === 0 }));
  }
  zones.push({ id: 'island', home: null, holder: null, name: 'Lonely Island', capital: false });
  return zones;
}

function historyPoint(c: CountryState, t: number) {
  return {
    t,
    treasury: c.treasury, energy: c.energy, food: c.food, support: c.support,
    prosperity: c.prosperity, security: c.security, wellbeing: c.wellbeing, environment: c.environment,
    carbon: c.carbon, index: nationalIndex(c),
  };
}

export { historyPoint };

/** Creates a new world in the lobby. All eight nations start as AI; placement makes some human. */
export function createWorld(opts: CreateWorldOptions): WorldState {
  // Strategic weapons are not part of this version: the setting is kept for saved worlds but is always off.
  const config: WorldConfig = { ...DEFAULT_CONFIG, ...opts.config, strategicWeapons: false };
  const pairing: Record<CountryId, SlotId> = { ...(PAIRINGS[config.pairing] ?? CLASSIC_WORLD), ...opts.pairing };
  const rng = new Rng(deriveSeed(opts.seed, 'setup'));
  const ids = IDEOLOGY_IDS.slice();
  const countries: Partial<Record<CountryId, CountryState>> = {};
  for (const id of ids) {
    const c = makeCountry(id, pairing[id], rng);
    // Karsova's Heritage Tariffs: a standing tariff on every partner.
    if (id === 'karsova') c.tariffs = ids.filter((x) => x !== id).map((x) => ({ target: x, until: 999, standing: true }));
    c.history.push(historyPoint(c, 0));
    countries[id] = c;
  }
  const crises: CrisisState[] = Object.values(CRISES).map((d) => ({ id: d.id, turn: d.turn, status: 'scheduled', responders: [] }));
  const carbon0 = ids.reduce((a, id) => a + (countries[id]?.carbon ?? 0), 0);
  const world: WorldState = {
    v: 1,
    id: opts.id,
    className: opts.className,
    createdAt: opts.now,
    seed: opts.seed >>> 0,
    rng: deriveSeed(opts.seed, 'play'),
    config,
    phase: 'lobby',
    turn: 0,
    clock: { stage: 'briefing', deadline: null, paused: false, remainingMs: null, ordersMs: DEFAULT_ORDERS_MS, briefingMs: DEFAULT_BRIEFING_MS },
    endRequested: false,
    roster: [],
    nations: ids,
    countries,
    zones: makeZones(ids, pairing),
    heat: {
      meter: HEAT.start, stage: 1, stageTurns: { 1: 0 }, carbon0Total: carbon0, cleanTechTurn: null,
      methaneFired: 0, diebackFired: false, innovationPrizes: 0, cleanTechPush: false, lastAdd: 0,
    },
    fund: {
      exists: false, createdTurn: null, balance: 0, formula: 'vulnerability', pledges: [], totalPledged: 0,
      totalPaidOut: 0, forestPaid: 0, payoutTurn: null, worldFinance: 0,
    },
    orders: {},
    trades: [],
    treatyProposals: [],
    treaties: [],
    wars: [],
    assembly: null,
    assemblyHistory: [],
    crises,
    island: { discovered: false, developer: null, protected: false },
    news: [],
    lastEvents: [],
    counters: { proposal: 0, treaty: 0, news: 0, item: 0 },
    flags: { pandemicContained: null, healthCompact: false, truceUntil: 0, lastAttackTurn: -99, coalitionItemPending: false, pledgedLastSession: [] },
    final: null,
  };
  ensureAgendas(world);
  return world;
}

/** Normalizes a roster line to "First L." (first name plus last initial only). */
export function normalizeName(line: string): string {
  // A pasted email keeps only its local part, split into name pieces ("john.smith@school.org" -> "John S.").
  const at = line.indexOf('@');
  const text = at >= 0 ? line.slice(0, at).replace(/[._]+/g, ' ') : line;
  const words = text.replace(/[^\p{L}\p{M}\s'.-]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const cap = (w: string) => w.charAt(0).toLocaleUpperCase() + w.slice(1);
  // Only the first name is kept: dots split it, and a hyphenated first name keeps at most two parts.
  const firstWord = ((words[0] as string).split('.').find((x) => x.length > 0) ?? '').split('-').filter(Boolean).slice(0, 2).join('-');
  const first = cap(firstWord).slice(0, 20);
  if (!first) return '';
  if (words.length === 1) return first;
  const last = (words[words.length - 1] as string).replace(/[.'-]/g, '');
  return last ? `${first} ${last.charAt(0).toLocaleUpperCase()}.` : first;
}

/** Adds students (names are normalized and de-duplicated). Returns the new state and the new entries. */
export function addRoster(state: WorldState, names: string[]): { state: WorldState; added: RosterEntry[] } {
  const s = structuredClone(state);
  const added: RosterEntry[] = [];
  let next = s.roster.reduce((m, r) => Math.max(m, Number.parseInt(r.id.slice(1), 10) || 0), 0) + 1;
  const taken = new Set(s.roster.map((r) => r.name));
  for (const raw of names) {
    let name = normalizeName(raw);
    if (!name) continue;
    if (taken.has(name)) {
      let k = 2;
      while (taken.has(`${name} (${k})`)) k++;
      name = `${name} (${k})`;
    }
    taken.add(name);
    const entry: RosterEntry = {
      id: `s${next++}`, name, country: null, roles: [], present: true, flex: false, seat: 0, hosVolunteer: false, card: null,
    };
    s.roster.push(entry);
    added.push(entry);
  }
  // Late joiners after placement go to the smallest team as flex members.
  if (s.phase !== 'lobby' && s.phase !== 'quiz' && s.phase !== 'placement') {
    const human = s.nations.filter((id) => s.countries[id] && !s.countries[id]?.isAI);
    for (const e of added) {
      if (human.length === 0) break;
      const sizes = human.map((id) => ({ id, n: s.roster.filter((r) => r.country === id).length }));
      sizes.sort((a, b) => a.n - b.n || a.id.localeCompare(b.id));
      const target = sizes[0]?.id;
      if (!target) break;
      e.country = target;
      e.flex = true;
      e.card = 'balance';
      e.seat = s.roster.filter((r) => r.country === target).length;
      redealRoles(s, target);
    }
  }
  return { state: s, added };
}

/** Low-carbon checks for shrinking the world (whole AI pairings only). */
function validSubWorld(s: WorldState, keep: CountryId[]): boolean {
  const cs = keep.map((id) => s.countries[id]).filter((c): c is CountryState => !!c);
  const vulnerable = cs.filter((c) => SLOTS[c.slot].mostVulnerable).length;
  const avg = cs.reduce((a, c) => a + c.carbon, 0) / Math.max(1, cs.length);
  return vulnerable >= 2 && avg >= 2.25 && avg <= 2.75;
}

/**
 * Drops AI pairings until the world has `n` nations, keeping the averages the design requires. `ai` lists the AI
 * nations lowest class fit first; of the drop sets that keep the world valid, the one that drops the lowest-fit
 * pairings wins (smallest sum of positions in `ai`, then the earliest positions).
 */
export function shrinkWorld(s: WorldState, n: number, ai: CountryId[]): CountryId[] {
  const drop = s.nations.length - n;
  if (drop <= 0) return [];
  const candidates = ai.filter((id, k) => s.nations.includes(id) && ai.indexOf(id) === k);
  const combos: number[][] = [];
  const walk = (start: number, pick: number[]) => {
    if (pick.length === drop) {
      combos.push(pick.slice());
      return;
    }
    for (let k = start; k < candidates.length; k++) walk(k + 1, [...pick, k]);
  };
  walk(0, []);
  const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
  combos.sort((a, b) => sum(a) - sum(b) || a.join(',').localeCompare(b.join(','), 'en', { numeric: true }));
  for (const combo of combos) {
    const ids = combo.map((k) => candidates[k] as CountryId);
    const keep = s.nations.filter((id) => !ids.includes(id));
    if (validSubWorld(s, keep)) return ids;
  }
  return [];
}

export interface PublishResult {
  state: WorldState;
  errors: string[];
}

/** Applies a teacher's placement publication: seats, roles, human and AI nations. Contains no quiz data. */
export function applyPublication(state: WorldState, pub: PlacementPublication): PublishResult {
  const s = structuredClone(state);
  const errors: string[] = [];
  const human = pub.human.filter((c, k, a) => IDEOLOGY_IDS.includes(c) && a.indexOf(c) === k);
  if (human.length === 0) errors.push('Choose at least one human nation.');
  const seen = new Set<string>();
  for (const a of pub.assignments) {
    const r = s.roster.find((x) => x.id === a.student);
    if (!r) {
      errors.push(`Unknown student ${a.student}.`);
      continue;
    }
    if (!human.includes(a.country)) errors.push(`${r.name} is placed in a nation that is not human-played.`);
    if (seen.has(a.student)) errors.push(`${r.name} is placed twice.`);
    seen.add(a.student);
  }
  if (errors.length) return { state, errors };
  for (const id of s.nations) {
    const c = s.countries[id];
    if (c) c.isAI = !human.includes(id);
  }
  const seatCount = new Map<CountryId, number>();
  for (const a of pub.assignments) {
    const r = s.roster.find((x) => x.id === a.student);
    if (!r) continue;
    const seat = seatCount.get(a.country) ?? 0;
    seatCount.set(a.country, seat + 1);
    r.country = a.country;
    r.roles = a.roles.slice();
    r.card = a.card;
    r.seat = seat;
  }
  // Students not in the publication (for example, added late) join the smallest team.
  for (const r of s.roster) {
    if (r.country) continue;
    const target = human.slice().sort((x, y) => (seatCount.get(x) ?? 0) - (seatCount.get(y) ?? 0) || x.localeCompare(y))[0];
    if (!target) break;
    r.country = target;
    r.flex = true;
    r.card = 'balance';
    r.seat = seatCount.get(target) ?? 0;
    seatCount.set(target, r.seat + 1);
  }
  for (const c of human) {
    const team = s.roster.filter((r) => r.country === c);
    if (team.length > 0 && team.every((r) => r.roles.length === 0)) redealRoles(s, c);
    else if (team.length > 0 && !team.some((r) => r.roles.includes('head'))) redealRoles(s, c);
  }
  if (s.config.nNations < s.nations.length) {
    const aiIds = s.nations.filter((id) => !human.includes(id));
    // Lowest class fit first, as the teacher's placement ranked them; any AI nation it did not rank goes last.
    const byFit = [...(pub.aiByFit ?? []).filter((id) => aiIds.includes(id)), ...aiIds.filter((id) => !(pub.aiByFit ?? []).includes(id))];
    const dropped = shrinkWorld(s, s.config.nNations, byFit);
    if (dropped.length) {
      s.nations = s.nations.filter((id) => !dropped.includes(id));
      for (const id of dropped) delete s.countries[id];
      s.zones = s.zones.filter((z) => z.home === null || !dropped.includes(z.home));
      for (const c of Object.values(s.countries)) {
        if (!c) continue;
        c.tariffs = c.tariffs.filter((t) => !dropped.includes(t.target));
      }
      s.heat.carbon0Total = s.nations.reduce((a, id) => a + (s.countries[id]?.carbon ?? 0), 0);
    }
  }
  s.phase = 'turn0';
  s.turn = 0;
  return { state: s, errors };
}
